import { Component, Input, input, output, signal, viewChild } from '@angular/core';
import { GoogleMap, MapInfoWindow, MapMarker } from '@angular/google-maps';
import { StationLongDto } from '@api/rest';
import { appConfig } from 'src/assets/config/app.config';


@Component({
    selector: 'app-google-map',
    templateUrl: './google-map.component.html',
    styleUrls: ['./google-map.component.scss'],
    imports: [GoogleMap, MapMarker]
})
export class GoogleMapComponent {
  public zoom = 11;
  public options: google.maps.MapOptions = appConfig.googleMaps.mapOptions;
  public markerOptions: google.maps.MarkerOptions = { icon: 'assets/icons/location.svg' };
  public markerOptionsActive: google.maps.MarkerOptions = { icon: 'assets/icons/location-selected.svg' };
  public markerOptionsOwnPosition: google.maps.MarkerOptions = { icon: 'assets/icons/my_location.svg' };

  public center = signal<google.maps.LatLngLiteral>(undefined);
  public contactSelected  = signal<StationLongDto>(undefined);
  public ownPosition = signal<google.maps.LatLngLiteral>(undefined);

  private readonly centerFallbackFh: google.maps.LatLngLiteral = { lat: 48.15806967688501, lng: 16.382443657662837 };

  readonly map = viewChild(GoogleMap);
  readonly infoWindow = viewChild(MapInfoWindow);

  readonly contacts = input<StationLongDto[]>(undefined);
  @Input() set selected(selectedContact: StationLongDto) {
    this.center.set(selectedContact?.geolat && selectedContact?.geolong
      ? { lat: selectedContact.geolat, lng: selectedContact.geolong }
      : undefined);
    this.contactSelected.set(selectedContact);
  }

  readonly selectContact = output<StationLongDto>();

  constructor() {
    setTimeout(() => {
      this.setCurrentLocationAsCenter();
    }, 1000);
  }

  private degreesToRadians(degrees: number): number {
      const radians = (degrees * Math.PI) / 180;
      return radians;
  }

  private calcCoordsDistance(starting: google.maps.LatLngLiteral, destination: google.maps.LatLngLiteral): number  {
    const startingLat = this.degreesToRadians(starting.lat);
    const startingLong = this.degreesToRadians(starting.lng);
    const destinationLat = this.degreesToRadians(destination.lat);
    const destinationLong = this.degreesToRadians(destination.lng);

    // Radius of the Earth in kilometers
    const radius: number = 6571;

    // Haversine equation
    const distanceInKilometers: number = Math.acos(Math.sin(startingLat) * Math.sin(destinationLat) +
    Math.cos(startingLat) * Math.cos(destinationLat) *
    Math.cos(startingLong - destinationLong)) * radius;

    return Math.floor(distanceInKilometers * 100) / 100;
  }

  private selectNearestStation(lat: number, lng: number) {
    let nearestContact: StationLongDto;
    let distance: number = 999999999999999;
    for (const contact of this.contacts().filter(c => c.geolat && c.geolong)) {
      const calculatedDistance = this.calcCoordsDistance({lat,lng}, {lat: contact.geolat, lng: contact.geolong});

      if (calculatedDistance < distance) {
        nearestContact = contact;
        distance = calculatedDistance;
      }
    }

    if (nearestContact) {
      this.contactSelected.set(nearestContact);
      this.selectContact.emit(this.contactSelected());
    }
  }

  private setCurrentLocationAsCenter() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(position => {
        if (position?.coords?.latitude && position?.coords?.longitude) {
          this.ownPosition.set({lat: position.coords.latitude, lng: position.coords.longitude});
          this.center.set(this.ownPosition());
        } else {
          this.center.set(this.centerFallbackFh);
        }
      },
        (error) => {
          console.log('MapError', error);
          this.center.set(this.centerFallbackFh);
        });
    } else {
      this.center.set(this.centerFallbackFh);
      console.log("Geolocation is not supported by this browser.");
    }
  }

  onMarkerClick(contact: StationLongDto) {
    this.selectContact.emit(contact);
  }

  protected getValidContacts(): { contact: StationLongDto; position: google.maps.LatLngLiteral }[] {
    return (this.contacts() || [])
      .map(contact => {
        const lat = Number(contact.geolat);
        const lng = Number(contact.geolong);

        if (isNaN(lat) || isNaN(lng)) return null;

        return {
          contact,
          position: { lat, lng }
        };
      })
      .filter(Boolean);
  }
}
